import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tent, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email.trim(), password);
    setBusy(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Hero panel */}
      <div className="hidden md:flex relative bg-primary text-primary-foreground p-10 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1591712964093-d98e3dcc7dfc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxzY291dCUyMGNhbXBpbmclMjBvdXRkb29ycyUyMGhpa2luZyUyMGZvcmVzdCUyMG5hdHVyZXxlbnwwfHx8fDE3ODg2MTExOTd8MA&ixlib=rb-4.1.0&q=85')",
          backgroundSize: "cover", backgroundPosition: "center"
        }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-accent text-accent-foreground flex items-center justify-center">
            <Tent className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-xl" style={{ fontFamily: "Outfit" }}>Lough Scouts</div>
            <div className="text-xs uppercase tracking-widest opacity-80">5th Cork Group</div>
          </div>
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="text-4xl lg:text-5xl font-extrabold leading-tight" style={{ fontFamily: "Outfit" }}>
            Keep every Beaver, Cub, Scout & Venture in check.
          </h1>
          <p className="opacity-85 max-w-md">
            A single hub for your membership register, weekly attendance, fees and the group's payments ledger — built for Irish scout groups.
          </p>
          <div className="flex items-center gap-2 text-sm opacity-80">
            <ShieldCheck className="w-4 h-4" /> Secure sign-in for group leaders only
          </div>
        </div>
        <div className="relative z-10 text-xs opacity-70">
          Baden-Powell believed “The most worth-while thing is to try to put happiness into the lives of others.”
        </div>
      </div>

      {/* Login form */}
      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md card-shadow-soft">
          <CardHeader>
            <CardTitle className="text-2xl" style={{ fontFamily: "Outfit" }}>Sign in</CardTitle>
            <p className="text-sm text-muted-foreground">Access your group's register</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="min-h-[44px]" autoComplete="email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} className="min-h-[44px]" autoComplete="current-password" />
              </div>
              {error && <div data-testid="login-error" className="text-sm text-destructive bg-destructive/10 p-2.5 rounded-md">{error}</div>}
              <Button type="submit" data-testid="login-submit" className="w-full min-h-[44px]" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>


            <div className="mt-4 text-center text-sm">
              <Link to="/forgot-password" className="text-primary font-semibold hover:underline" data-testid="link-forgot">Forgot your password?</Link>
            </div>
            <div className="mt-5 text-center text-xs text-muted-foreground" data-testid="invite-only-note">
              Access is by invitation only. Contact the Group Scout Leader if you need an account.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
